import { z } from 'zod'
import { idSchema, isoDateTimeSchema, minorSchema, optionalText, text } from './common.js'

/**
 * Four directions of money moving between the company and a worker, kept as
 * one ledger per worker rather than several separate lists — a petrol
 * receipt, a cash advance, a late-arrival deduction, and overtime pay all
 * affect the same "who owes whom" number.
 *
 *   advance:       the worker took money FROM the company. Worker owes it back.
 *   reimbursement: the worker paid FOR the company (fuel, a part, a toll).
 *                  The company owes it back.
 *   deduction:     money withheld from salary — currently only ever the
 *                  late-arrival policy, auto-created from a 'late' StaffEvent.
 *                  Worker owes it, same direction as an advance.
 *   overtime:      money owed to the worker for time worked past close time,
 *                  auto-created from an 'overtime' StaffEvent.
 *                  The company owes it, same direction as a reimbursement.
 */
export const STAFF_TRANSACTION_TYPES = ['advance', 'reimbursement', 'deduction', 'overtime'] as const
export type StaffTransactionType = (typeof STAFF_TRANSACTION_TYPES)[number]
export const staffTransactionTypeSchema = z.enum(STAFF_TRANSACTION_TYPES)

export const STAFF_TRANSACTION_TYPE_LABELS: Record<StaffTransactionType, string> = {
  advance: 'Took cash',
  reimbursement: 'Paid for the company',
  deduction: 'Salary deduction',
  overtime: 'Overtime pay',
}

const blankable = (max: number) => z.string().trim().max(max).default('')

const workerFields = {
  name: text(120),
  phone: blankable(32),
  notes: optionalText(2000),
  /** Null means the late-arrival policy has nothing to compute a rate from. */
  monthlySalaryMinor: z.number().int().min(0).nullable().default(null),
  /** Null means unassigned — not every worker has to belong to a department. */
  departmentId: idSchema.nullable().default(null),
}

export const workerInputSchema = z.object(workerFields)
export type WorkerInput = z.infer<typeof workerInputSchema>

export const workerSchema = z.object({
  ...workerFields,
  id: idSchema,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
})
export type Worker = z.infer<typeof workerSchema>

const staffTransactionFields = {
  type: staffTransactionTypeSchema,
  amountMinor: minorSchema.refine((v) => v > 0, 'Amount must be greater than zero'),
  /** What it was for — "Fuel for delivery run", "Advance for personal use". */
  reason: text(500),
  /** When they took or paid it, not when the record was typed in. */
  occurredAt: z.string().min(1),
  notes: optionalText(2000),
  /** Already paid back at the moment of saving, e.g. logging old history. */
  settled: z.boolean().default(false),
}

/** Server-computed metadata, never sent by the client, so it lives outside staffTransactionFields. */
const staffTransactionComputedFields = {
  /** Set when auto-created from a 'late' StaffEvent; null for anything typed by hand. */
  sourceStaffEventId: idSchema.nullable(),
}

export const staffPaymentInputSchema = z.object({
  amountMinor: minorSchema.refine((v) => v > 0, 'Amount must be greater than zero'),
  paidAt: z.string().min(1),
  notes: optionalText(1000),
})
export type StaffPaymentInput = z.infer<typeof staffPaymentInputSchema>

export const staffTransactionPaymentSchema = z.object({
  id: idSchema,
  transactionId: idSchema,
  amountMinor: z.number().int(),
  paidAt: z.string(),
  notes: z.string().nullable(),
  createdAt: isoDateTimeSchema,
})
export type StaffTransactionPayment = z.infer<typeof staffTransactionPaymentSchema>

export const staffTransactionInputSchema = z.object(staffTransactionFields)
export type StaffTransactionInput = z.infer<typeof staffTransactionInputSchema>

export const staffTransactionSchema = z.object({
  id: idSchema,
  workerId: idSchema,
  type: staffTransactionTypeSchema,
  amountMinor: z.number().int(),
  reason: z.string(),
  occurredAt: z.string(),
  /** Null while pending; set the moment it is settled. */
  settledAt: z.string().nullable(),
  notes: z.string().nullable(),
  ...staffTransactionComputedFields,
  createdAt: isoDateTimeSchema,
  updatedAt: isoDateTimeSchema,
  /** Payments attached to this transaction, oldest first. */
  payments: z.array(staffTransactionPaymentSchema),
  /** Sum of all payment amounts. */
  paidMinor: z.number().int(),
  /** Amount still unpaid (0 once settled, even if not fully paid). */
  remainingMinor: z.number().int(),
})
export type StaffTransaction = z.infer<typeof staffTransactionSchema>

export interface WorkerListItem extends Worker {
  /** Pending balance only. Positive: worker owes the company. Negative: company owes the worker. */
  balanceMinor: number
  /** Denormalised for display — null when unassigned or the department was removed. */
  departmentName: string | null
}

export interface WorkerDetail extends Worker {
  balanceMinor: number
  transactions: StaffTransaction[]
  departmentName: string | null
}

/** Net pending balance: positive means the worker owes the company. */
export function computeStaffBalance(
  transactions: Pick<StaffTransaction, 'type' | 'amountMinor' | 'settledAt' | 'paidMinor'>[],
): number {
  return transactions.reduce((sum, t) => {
    if (t.settledAt !== null) return sum
    const remaining = Math.max(0, t.amountMinor - (t.paidMinor ?? 0))
    return sum + (t.type === 'reimbursement' || t.type === 'overtime' ? -remaining : remaining)
  }, 0)
}
